import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronsUpDown, Plus, Check, Lock, Settings2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Sidebar-top workspace switcher.
 * - Pro Unlimited: can create + switch freely
 * - Others: dropdown works, "Add workspace" shows upgrade CTA
 */
export default function WorkspaceSwitcher({ collapsed = false }) {
  const nav = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const { workspaces, activeId, activeWorkspace, canCreateMore, switchTo, create } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setCreating(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const initials = (activeWorkspace?.name || "W").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");

  const onSwitch = async (id) => {
    if (id === activeId) { setOpen(false); return; }
    setBusy(true);
    try {
      const res = await switchTo(id);
      await refreshUser();
      setOpen(false);
      if (res && !res.onboarded) {
        nav("/onboarding");
      } else {
        // Hard reload so all workspace-scoped data (dive brain, vault, dashboards) re-fetches cleanly.
        window.location.reload();
      }
    } catch {
      toast.error("Could not switch workspace");
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await create(newName.trim());
      await refreshUser();
      setOpen(false); setCreating(false); setNewName("");
      toast.success("Workspace created. Let's onboard it.");
      nav("/onboarding");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create workspace";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  if (!activeWorkspace) return null;

  // Distinguish owner rows from executor-member rows. Executors get an
  // "Executor" pill and the owner-name subtitle inside the dropdown, and
  // the "Add workspace" button is hidden when the caller owns ZERO of the
  // workspaces they can access (pure-executor account).
  const ownedCount = (workspaces || []).filter(w => (w.role || "owner") === "owner").length;
  const activeIsMember = (activeWorkspace?.role || "owner") === "executor";

  return (
    <div ref={ref} className="relative" data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-button"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#F1F5F9] transition-colors border border-transparent hover:border-[#E2E8F0]"
      >
        <div className="w-7 h-7 rounded bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center font-heading font-bold text-[12px] shrink-0">
          {initials || "W"}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <div className="text-[10.5px] tracking-[0.2em] uppercase font-bold text-[#64748B]">
                {activeIsMember ? "Executor · Workspace" : "Workspace"}
              </div>
              <div className="text-[13px] font-semibold text-[#0A2342] truncate" data-testid="workspace-active-name">{activeWorkspace.name}</div>
              {activeIsMember && activeWorkspace.owner_name && (
                <div className="text-[10.5px] text-[#64748B] truncate">Owner: {activeWorkspace.owner_name}</div>
              )}
            </div>
            <ChevronsUpDown className="w-3.5 h-3.5 text-[#64748B] shrink-0" />
          </>
        )}
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-50 animate-fade-up" data-testid="workspace-menu">
          <div className="px-3 py-2 border-b border-[#F1F5F9]">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Your workspaces</div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {workspaces.map((w) => {
              const isMember = (w.role || "owner") === "executor";
              return (
                <button
                  key={w.id}
                  onClick={() => onSwitch(w.id)}
                  disabled={busy}
                  data-testid={`workspace-item-${w.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left hover:bg-[#F1F5F9] ${w.id === activeId ? "bg-[#F8FAFC]" : ""}`}
                >
                  <div className={`w-6 h-6 rounded flex items-center justify-center font-heading font-bold text-[10.5px] ${isMember ? "bg-[#F0FDFA] text-[#0F766E] border border-[#4FD1C5]/40" : "bg-[#0A2342] text-[#4FD1C5]"}`}>
                    {(w.name || "W").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A2342] truncate flex items-center gap-1.5">
                      <span className="truncate">{w.name}</span>
                      {isMember && <span className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#0F766E] bg-[#ECFDF5] rounded px-1.5 py-0.5 border border-[#A7F3D0] shrink-0">Executor</span>}
                    </div>
                    {isMember && w.owner_name ? (
                      <div className="text-[10.5px] text-[#64748B] truncate">Owner: {w.owner_name}</div>
                    ) : (!w.onboarded && <div className="text-[10.5px] text-[#B45309]">Needs onboarding</div>)}
                  </div>
                  {w.id === activeId && <Check className="w-3.5 h-3.5 text-[#0F766E]" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-[#F1F5F9] py-1">
            {ownedCount === 0 ? (
              // Pure-executor account — no "Add workspace", no "Manage".
              <div className="px-3 py-2 text-[11.5px] text-[#64748B]">
                You&apos;re an executor on {workspaces.length} workspace{workspaces.length === 1 ? "" : "s"}. Contact the owner to be added to another.
              </div>
            ) : creating ? (
              <form onSubmit={onCreate} className="px-3 py-2 space-y-2">
                <input
                  autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                  data-testid="workspace-new-name-input"
                  placeholder="e.g. Freediving Brand" maxLength={60}
                  className="w-full text-[13px] px-2.5 py-1.5 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#4FD1C5]"
                />
                <div className="flex items-center gap-2">
                  <button type="submit" disabled={busy || !newName.trim()} data-testid="workspace-create-submit" className="dms-btn-primary px-3 py-1.5 text-[12px]">Create</button>
                  <button type="button" onClick={() => { setCreating(false); setNewName(""); }} className="text-[12px] text-[#64748B] px-2 py-1.5 hover:text-[#0A2342]">Cancel</button>
                </div>
              </form>
            ) : canCreateMore ? (
              <button
                onClick={() => setCreating(true)}
                data-testid="workspace-create-button"
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#0A2342] hover:bg-[#F1F5F9]"
              >
                <Plus className="w-3.5 h-3.5" /> Add workspace
              </button>
            ) : (
              <button
                onClick={() => { setOpen(false); nav("/billing"); }}
                data-testid="workspace-upgrade-cta"
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#0A2342] hover:bg-[#F1F5F9]"
                title="Multiple workspaces unlock on Pro Unlimited"
              >
                <Lock className="w-3.5 h-3.5 text-[#64748B]" />
                <span className="flex-1 text-left">
                  <div>Add workspace</div>
                  <div className="text-[10.5px] text-[#64748B] font-normal">Pro Unlimited only</div>
                </span>
              </button>
            )}
            <button
              onClick={() => { setOpen(false); nav("/workspaces"); }}
              data-testid="workspace-manage-link"
              className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0A2342] ${ownedCount === 0 ? "hidden" : ""}`}
            >
              <Settings2 className="w-3.5 h-3.5" /> Manage workspaces
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
